import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { t, vehicleColor, vehicleColors } from '@aquabox/shared';
import {
  useMe,
  useOrderMutations,
  useOrders,
  useRegions,
  useVehiclePositions,
  useVehicles,
} from '../api/queries';
import type { Order, VehiclePosition } from '../api/types';
import { RegionMatchBadge } from '../components/Pills';
import { useToast } from '../components/Toast';
import { getTileConfig } from '../lib/map-tiles';
import {
  ASSIGNED_ORDER_STATUSES,
  buildRegionShapes,
  orderPoint,
  PENDING_ORDER_STATUSES,
  type HoodFeature,
  type RegionShape,
} from '../lib/map-layers';

/** Atanmamış sipariş işaretçisi (nötr gri). */
const UNASSIGNED_ORDER_COLOR = '#8A94A3';

/** Mahalle poligonları statik dosya (~1 MB); katman ilk açıldığında bir kez yüklenir, oturumca saklanır. */
function useHoodFeatures(enabled: boolean) {
  return useQuery({
    queryKey: ['bolu-hoods'],
    enabled,
    staleTime: Infinity,
    gcTime: Infinity,
    queryFn: async () => {
      const res = await fetch(`${import.meta.env.BASE_URL}maps/bolu-mahalleler.geojson`);
      if (!res.ok) throw new Error(`bolu-mahalleler.geojson: ${res.status}`);
      return ((await res.json()) as { features?: HoodFeature[] }).features ?? [];
    },
  });
}

/** Bolu merkez — hiç konum yokken haritanın açılacağı yer. */
const BOLU: [number, number] = [40.7355, 31.6061];

/**
 * Harita (§3.11).
 *
 * Konumlar sunucudan 15 saniyede bir gelir; panel hiçbir şey hesaplamaz, "sinyal yok" kararını
 * da sunucu verir (son kayıt 3 dakikadan eskiyse). Harita altlığı:
 *  - VITE_MAPTILER_KEY varsa MapTiler (mevcut davranış).
 *  - Yoksa ücretsiz OpenStreetMap karoları; "harita yok" uyarısı artık gösterilmez.
 *
 * Plaka renkleri `@aquabox/shared/vehicle-color` ile FNV-1a 32-bit paletinden gelir;
 * aynı plaka → aynı renk, yeni araç eklendiğinde diğerlerinin rengi kaymaz.
 */
export function Map() {
  const positions = useVehiclePositions();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const vehiclesQ = useVehicles();
  // Filo listesiyle çakışmasız renk; liste yokken ya da listede olmayan plakada tek-plaka rengi.
  const colorOf = useMemo(() => {
    const plates = (vehiclesQ.data?.items ?? []).filter((v) => v.isActive).map((v) => v.plate);
    const m = vehicleColors(plates);
    return (plate: string): string => m.get(plate) ?? vehicleColor(plate);
  }, [vehiclesQ.data]);
  const me = useMe();
  const canAssign = me.data?.role === 'OWNER' || me.data?.role === 'MANAGER';
  const [layers, setLayers] = useState({ vehicles: true, orders: true, regions: false });
  const [popupOrderId, setPopupOrderId] = useState<string | null>(null);
  // HARİTA ŞEKLİ (`view=map`): sunucu yalnız haritanın çizdiği alanları döner — telefon, ham
  // yük, kalem ve olaylar YOK (KVKK veri azaltma). Süzgeç ve sayfa DayBoard ile AYNI; sorgu
  // anahtarı AYRIŞIR (`view` parametresi farklı), yani harita kendi önbelleğini tutar.
  // Ek istek ve ek refetch yok (15 sn).
  const pendingQ = useOrders(
    { status: PENDING_ORDER_STATUSES, pageSize: '100', view: 'map' },
    layers.orders,
  );
  const assignedQ = useOrders(
    { status: ASSIGNED_ORDER_STATUSES, pageSize: '100', view: 'map' },
    layers.orders,
  );
  const regionsQ = useRegions();
  const hoodsQ = useHoodFeatures(layers.regions);
  const mapOrders = useMemo(
    () =>
      layers.orders
        ? [...(pendingQ.data?.items ?? []), ...(assignedQ.data?.items ?? [])].filter(
            (o) => orderPoint(o) !== null,
          )
        : [],
    [layers.orders, pendingQ.data, assignedQ.data],
  );
  const regionShapes = useMemo(
    () =>
      layers.regions && hoodsQ.data
        ? buildRegionShapes(hoodsQ.data, regionsQ.data?.items ?? [])
        : [],
    [layers.regions, hoodsQ.data, regionsQ.data],
  );
  const popupOrder = mapOrders.find((o) => o.id === popupOrderId) ?? null;
  // Sipariş listeden düşünce ya da katman kapanınca popup kalmasın (eski id ile geri gelmesin).
  useEffect(() => {
    if (popupOrderId !== null && !popupOrder) setPopupOrderId(null);
  }, [popupOrderId, popupOrder]);
  const ordersTotal = (pendingQ.data?.total ?? 0) + (assignedQ.data?.total ?? 0);
  const ordersShown = (pendingQ.data?.items.length ?? 0) + (assignedQ.data?.items.length ?? 0);
  const ordersTruncated = layers.orders && ordersTotal > ordersShown;
  const rows = layers.vehicles ? (positions.data ?? []) : [];
  const selected = rows.find((r) => r.vehicle.id === selectedId) ?? rows[0] ?? null;

  return (
    <div style={{ padding: '28px 32px 36px', display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div
        className="section-grid"
        style={
          {
            '--section-cols': '280px 1fr 320px',
            '--section-gap': '16px',
            alignItems: 'stretch',
            minHeight: 640,
          } as React.CSSProperties
        }
      >
        <VehicleList
          rows={rows}
          colorOf={colorOf}
          selectedId={selected?.vehicle.id ?? null}
          onSelect={setSelectedId}
        />
        <div className="card" style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
          <LeafletMap
            rows={rows}
            colorOf={colorOf}
            selectedId={selected?.vehicle.id ?? null}
            orders={mapOrders}
            regionShapes={regionShapes}
            onOrderClick={setPopupOrderId}
          />
          <LayerControl layers={layers} onChange={setLayers} />
          {ordersTruncated && (
            <span
              role="status"
              style={{
                position: 'absolute',
                right: 14,
                top: 12,
                fontSize: 12,
                background: 'var(--surface)',
                color: 'var(--danger)',
                padding: '5px 10px',
                borderRadius: 'var(--r-pill)',
                zIndex: 500,
              }}
            >
              {t('map.ordersTruncated', { total: ordersTotal, shown: ordersShown })}
            </span>
          )}
          {popupOrder && (
            <OrderPopup
              order={popupOrder}
              vehicles={(vehiclesQ.data?.items ?? []).filter((v) => v.isActive)}
              canAssign={canAssign}
              onClose={() => setPopupOrderId(null)}
            />
          )}
          <span
            style={{
              position: 'absolute',
              right: 14,
              bottom: 12,
              fontSize: 12,
              color: 'var(--text-secondary)',
              background: 'var(--surface)',
              padding: '5px 10px',
              borderRadius: 'var(--r-pill)',
              zIndex: 500,
            }}
          >
            {t('map.refresh')}
          </span>
        </div>
        <VehicleCard row={selected} />
      </div>
    </div>
  );
}

type Layers = { vehicles: boolean; orders: boolean; regions: boolean };

/** Üç katmanın bağımsız aç/kapa kutuları (haritanın sol üstü). */
function LayerControl({ layers, onChange }: { layers: Layers; onChange: (next: Layers) => void }) {
  const keys = ['vehicles', 'orders', 'regions'] as const;
  return (
    <fieldset
      aria-label={t('map.layers')}
      style={{
        position: 'absolute',
        left: 14,
        top: 12,
        zIndex: 500,
        background: 'var(--surface)',
        borderRadius: 'var(--r-row)',
        padding: '8px 12px',
        border: '1px solid var(--border)',
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        fontSize: 12.5,
      }}
    >
      {keys.map((k) => (
        <label key={k} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={layers[k]}
            onChange={(e) => onChange({ ...layers, [k]: e.target.checked })}
          />
          {t(`map.layer.${k}`)}
        </label>
      ))}
    </fieldset>
  );
}

/**
 * Sipariş işaretçisi popup'ı. Telefon BİLEREK yok (KVKK veri azaltma): yalnız ad, kısa adres ve
 * bölge rozeti. Atama mevcut `assign` mutasyonuyla yapılır; "Ata" yalnız OWNER/MANAGER'a görünür,
 * asıl yetki kapısı sunucudadır. 409 dahil hata mesajı sunucudan olduğu gibi toast'a düşer.
 */
export function OrderPopup({
  order,
  vehicles,
  canAssign,
  onClose,
}: {
  order: Order;
  vehicles: { id: string; plate: string }[];
  canAssign: boolean;
  onClose: () => void;
}) {
  const { assign } = useOrderMutations();
  const toast = useToast();
  const [vehicleId, setVehicleId] = useState('');
  const no = order.externalId ?? order.id.slice(-6).toUpperCase();
  const address = order.address.length > 60 ? `${order.address.slice(0, 57)}…` : order.address;

  function submit(): void {
    if (!vehicleId) return;
    assign.mutate(
      { id: order.id, vehicleId },
      {
        onSuccess: () => {
          const plate = vehicles.find((v) => v.id === vehicleId)?.plate ?? '';
          toast(t('map.orderPopup.assigned', { plate }));
          onClose();
        },
        onError: (err: unknown) => {
          const message = (err as { message?: string } | null)?.message;
          toast(message && message.length > 0 ? message : t('error.internal'), 'danger');
        },
      },
    );
  }

  return (
    <div
      role="dialog"
      aria-label={t('map.orderPopup.title', { no })}
      className="card"
      style={{
        position: 'absolute',
        left: 14,
        bottom: 12,
        zIndex: 600,
        width: 280,
        padding: 14,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <strong>{t('map.orderPopup.title', { no })}</strong>
        <button aria-label={t('map.orderPopup.close')} onClick={onClose}>
          ×
        </button>
      </div>
      <div style={{ fontSize: 13 }}>{order.customerName}</div>
      <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{address}</div>
      <div style={{ fontSize: 12.5 }}>
        {order.vehicle?.plate ?? t('map.orderPopup.unassigned')} <RegionMatchBadge order={order} />
      </div>
      {canAssign && (
        <div style={{ display: 'flex', gap: 6 }}>
          <select
            aria-label={t('map.orderPopup.vehicle')}
            value={vehicleId}
            onChange={(e) => setVehicleId(e.target.value)}
            style={{ flex: 1, minWidth: 0 }}
          >
            <option value="">{t('map.orderPopup.pick')}</option>
            {vehicles.map((v) => (
              <option key={v.id} value={v.id}>
                {v.plate}
              </option>
            ))}
          </select>
          <button
            className="btn btn-primary"
            disabled={!vehicleId || assign.isPending}
            onClick={submit}
          >
            {t('map.orderPopup.assign')}
          </button>
        </div>
      )}
    </div>
  );
}

function VehicleList({
  rows,
  colorOf,
  selectedId,
  onSelect,
}: {
  rows: VehiclePosition[];
  colorOf: (plate: string) => string;
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <div
      className="card"
      style={{ width: '100%', maxWidth: 280, minWidth: 0, padding: 16, overflow: 'auto' }}
    >
      <div className="section-label" style={{ marginBottom: 10 }}>
        {t('map.vehicles')} · {rows.length}
      </div>
      {rows.map((r) => (
        <button
          key={r.vehicle.id}
          onClick={() => onSelect(r.vehicle.id)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            width: '100%',
            textAlign: 'left',
            padding: '10px 12px',
            borderRadius: 'var(--r-row)',
            background: r.vehicle.id === selectedId ? 'var(--primary-bg)' : 'transparent',
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: r.active && !r.stale ? 'var(--success)' : 'var(--text-secondary)',
            }}
          />
          <span
            style={{
              fontWeight: 700,
              fontSize: 13.5,
              color: '#fff',
              background: colorOf(r.vehicle.plate),
              borderRadius: 6,
              padding: '1px 8px',
            }}
          >
            {r.vehicle.plate}
          </span>
          <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
            {r.driver?.fullName ?? '—'}
          </span>
        </button>
      ))}
    </div>
  );
}

function VehicleCard({ row }: { row: VehiclePosition | null }) {
  const navigate = useNavigate();
  if (!row) {
    return (
      <div className="card" style={{ width: '100%', maxWidth: 320, minWidth: 0, padding: 20 }}>
        <p style={{ fontSize: 13.5, color: 'var(--text-secondary)' }}>{t('map.selectVehicle')}</p>
      </div>
    );
  }
  return (
    <div className="card" style={{ width: '100%', maxWidth: 320, minWidth: 0, padding: 20 }}>
      <div style={{ fontWeight: 700, fontSize: 19 }}>{row.vehicle.plate}</div>
      <div style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
        {row.driver?.fullName ?? '—'}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 13.5 }}>
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: row.stale ? 'var(--text-secondary)' : 'var(--success)',
          }}
        />
        {signalLabel(row)}
      </div>
      {/* Sefer durumu ve sıradaki müşteri. `nextStop` yoksa satır GÖSTERİLMEZ. */}
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 6 }}>
        {row.active ? t('map.tripOpen') : t('map.tripNone')}
      </div>
      {row.nextStop && (
        <div style={{ marginTop: 6 }}>
          <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{t('map.nextStop')}</div>
          <div data-testid="next-stop" style={{ fontSize: 13.5 }}>
            {row.nextStop.customerName}
            {row.nextStop.neighborhood ? ` · ${row.nextStop.neighborhood}` : ''}
          </div>
        </div>
      )}
      <div style={{ borderTop: '1px solid var(--border)', margin: '14px 0' }} />
      <button
        className="btn btn-primary"
        style={{ width: '100%' }}
        disabled={!row.trip}
        onClick={() => {
          if (row.trip) void navigate('/seferler');
        }}
      >
        {t('map.goToTrip')}
      </button>
    </div>
  );
}

function signalLabel(row: VehiclePosition): string {
  if (row.ageSeconds === null) return t('map.neverSeen');
  if (!row.stale) return t('map.lastSeen', { n: row.ageSeconds });
  return t('map.noSignalSince', { n: Math.round(row.ageSeconds / 60) });
}

/**
 * Leaflet haritası.
 *
 * React'in dışında yaşayan bir kütüphane: harita bir kez kurulur, işaretçiler her veri
 * yenilenmesinde güncellenir. Her render'da haritayı yeniden yaratmak, patronun kaydırdığı
 * görüntüyü 15 saniyede bir başa sarardı.
 */
function LeafletMap({
  rows,
  colorOf,
  selectedId,
  orders,
  regionShapes,
  onOrderClick,
}: {
  rows: VehiclePosition[];
  colorOf: (plate: string) => string;
  selectedId: string | null;
  orders: Order[];
  regionShapes: RegionShape[];
  onOrderClick: (id: string) => void;
}) {
  const tileCfg = useMemo(() => getTileConfig(), []);
  const container = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const markers = useRef(new globalThis.Map<string, L.Marker>());
  const orderMarkers = useRef(new globalThis.Map<string, L.Marker>());
  // Araç → sıradaki müşteri kesik çizgisi (araç başına bir çizgi).
  const nextStopLines = useRef(new globalThis.Map<string, L.Polyline>());
  const regionLayer = useRef<L.GeoJSON | null>(null);
  // Tıklama geri çağırması ref'ten okunur: işaretçiler her render'da yeniden bağlanmaz.
  const onOrderClickRef = useRef(onOrderClick);
  onOrderClickRef.current = onOrderClick;

  useEffect(() => {
    if (!container.current || map.current) return;
    const instance = L.map(container.current, { attributionControl: true }).setView(BOLU, 13);
    L.tileLayer(tileCfg.url, { attribution: tileCfg.attribution, maxZoom: tileCfg.maxZoom }).addTo(
      instance,
    );
    map.current = instance;
    return () => {
      instance.remove();
      map.current = null;
      markers.current.clear();
      orderMarkers.current.clear();
      nextStopLines.current.clear();
      regionLayer.current = null;
    };
  }, []);

  useEffect(() => {
    const instance = map.current;
    if (!instance) return;
    const seen = new Set<string>();
    for (const row of rows) {
      if (!row.position) continue;
      seen.add(row.vehicle.id);
      const latLng: [number, number] = [row.position.lat, row.position.lng];
      const existing = markers.current.get(row.vehicle.id);
      const icon = plateIcon(row.vehicle.plate, row.active && !row.stale, colorOf);
      if (existing) {
        existing.setLatLng(latLng).setIcon(icon);
      } else {
        markers.current.set(row.vehicle.id, L.marker(latLng, { icon }).addTo(instance));
      }
    }
    for (const [id, marker] of markers.current) {
      if (seen.has(id)) continue;
      marker.remove();
      markers.current.delete(id);
    }
    // SIRADAKİ MÜŞTERİ ÇİZGİSİ: araç konumundan durak koordinatına kesik çizgi.
    // Konum ya da `nextStop` yoksa çizgi YOK; veri değişince eski çizgi kaldırılır (sızıntı olmaz).
    const seenStops = new Set<string>();
    for (const row of rows) {
      if (!row.position || !row.nextStop) continue;
      seenStops.add(row.vehicle.id);
      const points: [number, number][] = [
        [row.position.lat, row.position.lng],
        [row.nextStop.lat, row.nextStop.lng],
      ];
      const color = colorOf(row.vehicle.plate);
      const existing = nextStopLines.current.get(row.vehicle.id);
      if (existing) {
        existing.setLatLngs(points).setStyle({ color });
      } else {
        nextStopLines.current.set(
          row.vehicle.id,
          L.polyline(points, { color, weight: 2, dashArray: '6 8' }).addTo(instance),
        );
      }
    }
    for (const [id, line] of nextStopLines.current) {
      if (seenStops.has(id)) continue;
      line.remove();
      nextStopLines.current.delete(id);
    }
    const focus = rows.find((r) => r.vehicle.id === selectedId && r.position);
    if (focus?.position) instance.panTo([focus.position.lat, focus.position.lng]);
  }, [rows, selectedId, colorOf]);

  useEffect(() => {
    const instance = map.current;
    if (!instance) return;
    const seen = new Set<string>();
    for (const o of orders) {
      const point = orderPoint(o);
      if (!point) continue;
      seen.add(o.id);
      const color = o.vehicle ? colorOf(o.vehicle.plate) : UNASSIGNED_ORDER_COLOR;
      const icon = orderIcon(color);
      const existing = orderMarkers.current.get(o.id);
      if (existing) {
        existing.setLatLng(point).setIcon(icon);
      } else {
        const m = L.marker(point, { icon }).addTo(instance);
        m.on('click', () => onOrderClickRef.current(o.id));
        orderMarkers.current.set(o.id, m);
      }
    }
    for (const [id, marker] of orderMarkers.current) {
      if (seen.has(id)) continue;
      marker.remove();
      orderMarkers.current.delete(id);
    }
  }, [orders, colorOf]);

  useEffect(() => {
    const instance = map.current;
    if (!instance) return;
    regionLayer.current?.remove();
    regionLayer.current = null;
    if (regionShapes.length === 0) return;
    regionLayer.current = L.geoJSON(
      { type: 'FeatureCollection', features: regionShapes } as never,
      {
        style: (f) => {
          const color = (f?.properties as { color?: string } | null)?.color ?? '#8A94A3';
          return { color, weight: 2, fillColor: color, fillOpacity: 0.25 };
        },
        onEachFeature: (f, l) => {
          // Bölge adı veriden gelir: HTML değil metin düğümü olarak bağlanır.
          const el = document.createElement('span');
          el.textContent = (f.properties as { name?: string } | null)?.name ?? '';
          l.bindTooltip(el, { sticky: true, direction: 'top' });
        },
      },
    ).addTo(instance);
  }, [regionShapes]);

  return <div ref={container} style={{ width: '100%', height: 640 }} />;
}

/** Plaka pill işaretçisi (§3.11). Aktif araç `vehicleColor` ile, pasif araç nötr gri. */
function plateIcon(plate: string, active: boolean, colorOf: (plate: string) => string): L.DivIcon {
  const background = active ? colorOf(plate) : '#56677E';
  return L.divIcon({
    className: '',
    html: `<span style="display:inline-block;background:${background};color:#fff;font-weight:700;font-size:12px;padding:4px 8px;border-radius:999px;border:2px solid #fff;white-space:nowrap">${escapeHtml(plate)}</span>`,
    iconSize: [0, 0],
    iconAnchor: [0, 0],
  });
}

/** Sipariş işaretçisi: araç renginde küçük daire; atanmamışsa gri. */
function orderIcon(color: string): L.DivIcon {
  return L.divIcon({
    className: '',
    html: `<span style="display:block;width:14px;height:14px;background:${color};border:2px solid #fff;border-radius:50%;box-shadow:0 0 0 1px rgba(0,0,0,.35)"></span>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
  });
}

/** Plaka veriden gelir; HTML'e gömülmeden önce kaçırılır. */
function escapeHtml(value: string): string {
  return value.replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c,
  );
}
